/**
 * Map Configuration Service
 *
 * Centralizes all map-related configuration and constants
 * following Single Responsibility Principle
 */

export interface CityConfiguration {
  name: string;
  lat: number;
  lng: number;
  bounds?: {
    sw: { lat: number; lng: number };
    ne: { lat: number; lng: number };
  };
}

export interface MapConfiguration {
  token: string;
  defaultCenter: {
    lat: number;
    lng: number;
  };
  defaultZoom: number;
  minZoom: number;
  maxZoom: number;
  mapStyle: string;
  fallbackMapStyle: string;
  offlineMapStyle: object;
  maxTileCacheSize: number;
  fadeDuration: number;
  cities: CityConfiguration[];
}

export interface City {
  name: string;
  lat: number;
  lng: number;
}

export class MapConfigurationService {
  private static instance: MapConfigurationService;
  private configuration!: MapConfiguration;

  private constructor () {
    this.configuration = {
      token: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN) ?? '',
      defaultCenter: {
        lat: 24.7136, // Riyadh coordinates
        lng: 46.6753
      },
      defaultZoom: 10,
      minZoom: 3,
      maxZoom: 20,
      // Streets-v12 — Mapbox's standard streets style. Familiar Google-Maps-
      // like look: white road fills, coloured highways, full place labels,
      // POI icons. Streets-v11 is the safe fallback if v12 fails to load.
      mapStyle: 'mapbox://styles/mapbox/streets-v12',
      fallbackMapStyle: 'mapbox://styles/mapbox/streets-v11',
      offlineMapStyle: this.getOfflineMapStyleConfig(),
      maxTileCacheSize: 50,
      fadeDuration: 0,
      cities: this.getCitiesConfig()
    };
  }

  private getOfflineMapStyleConfig () {
    return {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors'
        }
      },
      layers: [
        {
          id: 'osm',
          type: 'raster',
          source: 'osm'
        }
      ]
    };
  }

  private getCitiesConfig (): CityConfiguration[] {
    return [
      {
        name: 'الرياض',
        lat: 24.7136,
        lng: 46.6753,
        bounds: {
          sw: {lat: 24.55, lng: 46.50},
          ne: {lat: 24.90, lng: 46.85}
        }
      },
      {name: 'جدة', lat: 21.4858, lng: 39.1925},
      {name: 'الدمام', lat: 26.4333, lng: 50.1033},
      {name: 'القصيم', lat: 26.3256, lng: 43.9740},
      {name: 'أبها', lat: 18.2164, lng: 42.5053},
      {name: 'المدينة المنورة', lat: 24.4672, lng: 39.6111},
      {name: 'الطائف', lat: 21.2703, lng: 40.4158},
      {name: 'تبوك', lat: 28.3998, lng: 36.5700},
      {name: 'بريدة', lat: 26.3260, lng: 43.9750},
      {name: 'خميس مشيط', lat: 18.3000, lng: 42.7333},
      {name: 'حائل', lat: 27.5114, lng: 41.6900},
      {name: 'الجبيل', lat: 27.0174, lng: 49.6225},
      {name: 'ينبع', lat: 24.0889, lng: 38.0619},
      {name: 'نجران', lat: 17.4917, lng: 44.1277},
      {name: 'الباحة', lat: 20.0129, lng: 41.4687},
      {name: 'عرعر', lat: 30.9753, lng: 41.0381}
    ];
  }

  public static getInstance (): MapConfigurationService {
    if (!MapConfigurationService.instance) {
      MapConfigurationService.instance = new MapConfigurationService();
    }
    return MapConfigurationService.instance;
  }

  public getConfiguration (): MapConfiguration {
    return {...this.configuration};
  }

  public getToken (): string {
    return this.configuration.token;
  }

  public getDefaultCenter (): {lat: number; lng: number} {
    return {...this.configuration.defaultCenter};
  }

  public getDefaultZoom (): number {
    return this.configuration.defaultZoom;
  }

  public getMinZoom (): number {
    return this.configuration.minZoom;
  }

  public getMaxZoom (): number {
    return this.configuration.maxZoom;
  }

  public getMapStyle (): string {
    return this.configuration.mapStyle;
  }

  public getFallbackMapStyle (): string {
    return this.configuration.fallbackMapStyle;
  }

  public getOfflineMapStyle (): object {
    return this.configuration.offlineMapStyle;
  }

  public getCities (): CityConfiguration[] {
    return [...this.configuration.cities];
  }

  public getCityByName (name: string): CityConfiguration | undefined {
    return this.configuration.cities.find(city => city.name === name);
  }

  public isTokenValid (): boolean {
    return !!this.configuration.token;
  }

  public validateCoordinates (lat: number, lng: number): boolean {
    return (
      lat !== undefined &&
      lng !== undefined &&
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  }

  public getMapProps () {
    return {
      mapboxAccessToken: this.configuration.token,
      mapStyle: this.configuration.mapStyle,
      attributionControl: false,
      reuseMaps: true,
      maxZoom: this.configuration.maxZoom,
      minZoom: this.configuration.minZoom,
      dragPan: true,
      scrollZoom: true,
      doubleClickZoom: true,
      touchZoomRotate: true,
      antialias: true,
      fadeDuration: this.configuration.fadeDuration,
      maxTileCacheSize: this.configuration.maxTileCacheSize,
      preserveDrawingBuffer: false,
      refreshExpiredTiles: false,
      // Add timeout and retry configuration
      transformRequest: (url: string, resourceType: string) => {
        // Add timeout to mapbox requests
        if (url.includes('api.mapbox.com')) {
          return {
            url,
            headers: {
              'Cache-Control': 'max-age=3600'
            }
          };
        }
        return {url};
      }
    };
  }

  public getAllCities (): CityConfiguration[] {
    return [...this.configuration.cities];
  }
}
